import {
  FC,
  ReactNode,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Popover } from '@headlessui/react';

type SidebarFlyoutProps = {
  buttonContent: ReactNode;
  buttonClassName?: string;
  buttonTestId?: string;
  buttonAriaLabel?: string;
  panelTestId?: string;
  children: ReactNode;
  onOpenChange?: (open: boolean) => void;
};

const SidebarFlyout: FC<SidebarFlyoutProps> = ({
  buttonContent,
  buttonClassName,
  buttonTestId,
  buttonAriaLabel,
  panelTestId,
  children,
  onOpenChange
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  return (
    <Popover className="w-full">
      {({ open, close }) => (
        <SidebarFlyoutBody
          open={open}
          close={close}
          buttonRef={buttonRef}
          coords={coords}
          setCoords={setCoords}
          buttonContent={buttonContent}
          buttonClassName={buttonClassName}
          buttonTestId={buttonTestId}
          buttonAriaLabel={buttonAriaLabel}
          panelTestId={panelTestId}
          onOpenChange={onOpenChange}
        >
          {children}
        </SidebarFlyoutBody>
      )}
    </Popover>
  );
};

type SidebarFlyoutBodyProps = SidebarFlyoutProps & {
  open: boolean;
  close: () => void;
  buttonRef: RefObject<HTMLButtonElement>;
  coords: { top: number; left: number };
  setCoords: (coords: { top: number; left: number }) => void;
};

const SidebarFlyoutBody: FC<SidebarFlyoutBodyProps> = ({
  open,
  close,
  buttonRef,
  coords,
  setCoords,
  buttonContent,
  buttonClassName,
  buttonTestId,
  buttonAriaLabel,
  panelTestId,
  children,
  onOpenChange
}) => {
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const maxTop = Math.max(8, window.innerHeight - 280);
    setCoords({
      top: Math.min(rect.top, maxTop),
      left: rect.right + 8
    });
  }, [open, buttonRef, setCoords]);

  return (
    <>
      <Popover.Button
        ref={buttonRef}
        className={buttonClassName}
        data-testid={buttonTestId}
        aria-label={buttonAriaLabel}
      >
        {buttonContent}
      </Popover.Button>
      {open &&
        createPortal(
          <Popover.Panel
            static
            data-testid={panelTestId}
            className="fixed z-50 w-56 rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
            style={{ top: coords.top, left: coords.left }}
            onClick={() => close()}
          >
            {children}
          </Popover.Panel>,
          document.body
        )}
    </>
  );
};

export default SidebarFlyout;
